import { useState } from "react";
import { useSite } from "@/context/SiteContext";
import { PageHead, Reveal } from "@/components/Motion";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";

const ROWS = [
  ["Hidden", "Nobody sees it except the family admin. Everything starts here by default."],
  ["Family only", "Only people who enter a valid family member code can see it, for a few hours at a time."],
  ["Public", "Anyone on the internet can see it. The admin must switch this on deliberately, item by item."],
];

export default function Privacy() {
  const { site } = useSite() || {};
  const credits = Object.values(site?.eras || {}).flat().filter((m) => /^CC/i.test(m.license || ""));
  const [f, setF] = useState({ name: "", contact: "", kind: "hide", message: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/public/privacy-request", f); toast.success("Your request was sent to the family admin."); setF({ name: "", contact: "", kind: "hide", message: "" }); }
    catch (er) { toast.error(errText(er)); }
    setBusy(false);
  };
  return (
    <div data-testid="privacy-page">
      <PageHead eyebrow="Privacy" title="Who can see what" line="In our family, the privacy of each person counts the most." />
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-28 grid gap-16">
        <div className="grid gap-5 md:grid-cols-3">
          {ROWS.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08} className="card-gold p-7"><p className="font-display text-2xl text-[#C9A24B]">{t}</p><p className="mt-3 text-sm leading-relaxed text-[#F6EFE0]/85">{d}</p></Reveal>
          ))}
        </div>
        <Reveal className="space-y-4 text-[#F6EFE0]/85 leading-relaxed">
          <p>Living relatives appear on the public tree only as an anonymous leaf, with no name, photo or dates, unless they agree to be shown. Children can never be made public.</p>
          <p>Business and investment information is never public. Personal pages open only with that person's own code and are hidden from search engines and link previews.</p>
          <p>Photos you upload have their location and device details removed. Private photos use links that expire after a few hours.</p>
        </Reveal>
        <Reveal>
          <form onSubmit={send} className="card-gold p-7 md:p-10 grid gap-5" data-testid="privacy-request-form">
            <h2 className="font-display text-3xl">Ask for your details to be hidden or removed</h2>
            <div className="grid gap-5 md:grid-cols-2">
              <div><label className="label">Your name</label><input className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="privacy-name-input" /></div>
              <div><label className="label">How to reach you (optional)</label><input className="field" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="privacy-contact-input" /></div>
            </div>
            <div><label className="label">Request</label>
              <select className="field" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="privacy-kind-select">
                <option value="hide">Hide my details</option><option value="remove">Remove my details</option><option value="other">Something else</option>
              </select></div>
            <div><label className="label">Message</label><textarea className="field min-h-[120px]" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required data-testid="privacy-message-input" /></div>
            <button className="btn-gold w-fit" disabled={busy} data-testid="privacy-submit-btn">{busy ? "Sending..." : "Send to the family admin"}</button>
          </form>
        </Reveal>
        {credits.length > 0 && (
          <details className="text-xs text-[#F6EFE0]/70" data-testid="picture-credits">
            <summary className="cursor-pointer label">Picture credits</summary>
            <ul className="mt-3 space-y-1">{credits.map((m) => <li key={m.id}>{m.title} · {m.credit} · {m.license} · <a href={m.source} target="_blank" rel="noreferrer" className="underline">source</a></li>)}</ul>
          </details>
        )}
      </section>
    </div>
  );
}
